import React, { FC } from 'react';

import { cn } from '@/shared/libs/utils';

import { FieldError, Label } from '../../primitives';

/** Native input is sr-only; custom ring/dot keeps checked+disabled readable. */
const radioVisualClassName = cn(
  'pointer-events-none flex size-4 shrink-0 items-center justify-center rounded-full border-2 bg-background transition-colors',
  'border-border',
  'peer-checked:border-primary',
  'peer-checked:[&>.form-radio-dot]:bg-primary',
  'peer-disabled:border-muted-foreground/55',
  'peer-disabled:peer-checked:border-primary',
  'peer-disabled:peer-checked:[&>.form-radio-dot]:bg-primary',
  'peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-background',
);

const radioNativeClassName = cn(
  'peer sr-only focus:outline-none',
  'disabled:cursor-not-allowed',
);

export interface FormRadioButtonOption {
  label: string;
  value: string;
  disabled?: boolean;
}

export interface FormRadioButtonsProps {
  value?: string;
  checked?: boolean;
  onChange?: (value: string) => void;
  options?: FormRadioButtonOption[];
  label?: string;
  disabled?: boolean;
  error?: string | string[];
  name?: string;
  direction?: 'row' | 'column';
}

export const FormRadioButtons: FC<FormRadioButtonsProps> = ({
  checked,
  direction = 'column',
  disabled,
  error,
  label,
  name,
  onChange,
  options,
  value,
}) => {
  const selectedValue = value ?? '';

  const handleChange = (val: string) => {
    onChange?.(val);
  };

  // Single radio button mode (no options array)
  if (!options) {
    return (
      <label
        className={cn(
          'flex items-center gap-2',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer',
          disabled && checked && 'opacity-[0.72]',
        )}>
        <input
          type="radio"
          name={name}
          value={value}
          checked={checked}
          onChange={() => handleChange(value!)}
          disabled={disabled}
          className={radioNativeClassName}
        />
        <span className={radioVisualClassName} aria-hidden>
          <span className="form-radio-dot size-2 shrink-0 rounded-full bg-transparent" />
        </span>
        {label && (
          <span
            className={cn(
              'text-sm',
              disabled ? 'text-muted-foreground' : 'text-foreground',
            )}>
            {label}
          </span>
        )}
      </label>
    );
  }

  // Multiple radio buttons mode
  return (
    <div className="flex flex-col gap-1.5">
      {label && <Label>{label}</Label>}
      <div
        className={cn(
          'flex gap-4',
          direction === 'column' ? 'flex-col' : 'flex-row',
        )}>
        {options.map((option) => (
          <label
            key={option.value}
            className={cn(
              'flex items-center gap-2',
              disabled || option.disabled
                ? 'cursor-not-allowed'
                : 'cursor-pointer',
              (disabled || option.disabled) &&
                selectedValue === option.value &&
                'opacity-[0.72]',
            )}>
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={selectedValue === option.value}
              onChange={() => handleChange(option.value)}
              disabled={disabled || option.disabled}
              className={radioNativeClassName}
            />
            <span className={radioVisualClassName} aria-hidden>
              <span className="form-radio-dot size-2 shrink-0 rounded-full bg-transparent" />
            </span>
            <span
              className={cn(
                'text-sm',
                disabled || option.disabled
                  ? 'text-muted-foreground'
                  : 'text-foreground',
              )}>
              {option.label}
            </span>
          </label>
        ))}
      </div>
      <FieldError error={error} />
    </div>
  );
};

FormRadioButtons.displayName = 'FormRadioButtons';
